import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Heart,
  Calendar,
  MapPin,
  Clock,
  Check,
  Send,
  Loader2,
  AlertCircle,
  UserPlus,
  UtensilsCrossed,
  MessageSquare
} from 'lucide-react';
import { wedding } from '../../services/api';

const MEAL_OPTIONS = ['Standard', 'Vegetarian', 'Vegan', 'Halal', 'Kosher', 'Other'];

function RSVPForm() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Per-event responses: { [eventId]: { attending: 'yes'|'no'|'maybe', meal_preference, dietary_notes } }
  const [responses, setResponses] = useState({});
  const [plusOne, setPlusOne] = useState(false);
  const [plusOneName, setPlusOneName] = useState('');
  const [message, setMessage] = useState('');

  const loadForm = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await wedding.getRsvpForm(token);
      setData(res.data);

      // Pre-populate from existing RSVPs if any
      if (res.data.rsvps && res.data.rsvps.length > 0) {
        const existing = {};
        res.data.rsvps.forEach((rsvp) => {
          existing[rsvp.event_id] = {
            attending: rsvp.status || 'pending',
            meal_preference: rsvp.meal_preference || 'Standard',
            dietary_notes: rsvp.dietary_notes || ''
          };
        });
        setResponses(existing);
        // Check for plus one info from guest
        if (res.data.guest?.plus_one_name) {
          setPlusOne(true);
          setPlusOneName(res.data.guest.plus_one_name);
        }
        if (res.data.guest?.message) {
          setMessage(res.data.guest.message);
        }
      }

      // Initialize empty responses for events without existing RSVPs
      if (res.data.events) {
        const init = {};
        res.data.events.forEach((event) => {
          if (!res.data.rsvps?.find((r) => r.event_id === event.id)) {
            init[event.id] = {
              attending: 'pending',
              meal_preference: 'Standard',
              dietary_notes: ''
            };
          }
        });
        setResponses((prev) => ({ ...init, ...prev }));
      }
    } catch (err) {
      console.error('RSVP form load error:', err);
      if (err.response?.status === 404) {
        setError('This RSVP link is invalid or has expired.');
      } else {
        setError('Failed to load RSVP form. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadForm();
  }, [loadForm]);

  const updateResponse = (eventId, field, value) => {
    setResponses((prev) => ({
      ...prev,
      [eventId]: {
        ...prev[eventId],
        [field]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);
      await wedding.submitRsvp(token, {
        responses,
        plus_one: plusOne,
        plus_one_name: plusOne ? plusOneName : null,
        message: message || null
      });
      setSubmitted(true);
    } catch (err) {
      console.error('RSVP submit error:', err);
      setError(err.response?.data?.error || 'Failed to submit RSVP. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':');
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return `${h12}:${m} ${ampm}`;
  };

  // Couple's custom colors or defaults
  const primaryColor = data?.wedding?.primary_color || '#f43f5e'; // rose-500

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-rose-500 animate-spin" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-rose-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Oops!</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    const weddingDate = data?.wedding?.wedding_date;
    const venueName = data?.wedding?.venue_name;
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">RSVP Submitted!</h1>
          <p className="text-gray-600 mb-8">
            Thank you, {data?.guest?.name}. Your response has been recorded.
          </p>

          {/* Wedding details summary */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 text-left mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Wedding Details</h2>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <Heart className="w-4 h-4 text-rose-500 flex-shrink-0" />
                <span>{data?.wedding?.couple_names || 'The Happy Couple'}</span>
              </div>
              {weddingDate && (
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <Calendar className="w-4 h-4 text-rose-500 flex-shrink-0" />
                  <span>{formatDate(weddingDate)}</span>
                </div>
              )}
              {venueName && (
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <MapPin className="w-4 h-4 text-rose-500 flex-shrink-0" />
                  <span>{venueName}</span>
                </div>
              )}
            </div>
          </div>

          {weddingDate && (
            <p className="text-xs text-gray-400">
              Add this date to your calendar so you don't forget!
            </p>
          )}
        </div>
      </div>
    );
  }

  const { guest, wedding: weddingInfo, events } = data;
  const coupleNames = weddingInfo?.couple_names || 'The Happy Couple';

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white">
      {/* Hero header */}
      <div
        className="text-center py-12 px-4"
        style={{
          background: `linear-gradient(135deg, ${primaryColor}15, ${primaryColor}05)`
        }}
      >
        <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${primaryColor}20` }}>
          <Heart className="w-6 h-6" style={{ color: primaryColor }} />
        </div>
        <p className="text-sm font-medium uppercase tracking-wider mb-2" style={{ color: primaryColor }}>
          You're Invited
        </p>
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{coupleNames}</h1>
        {weddingInfo?.wedding_date && (
          <p className="text-gray-600 flex items-center justify-center gap-2">
            <Calendar className="w-4 h-4" />
            {formatDate(weddingInfo.wedding_date)}
          </p>
        )}
        {weddingInfo?.venue_name && (
          <p className="text-gray-500 flex items-center justify-center gap-2 mt-1">
            <MapPin className="w-4 h-4" />
            {weddingInfo.venue_name}
          </p>
        )}
      </div>

      {/* Guest greeting */}
      <div className="max-w-lg mx-auto px-4 -mt-2 mb-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center">
          <p className="text-gray-600">Dear</p>
          <p className="text-xl font-semibold text-gray-900">{guest?.name}</p>
          <p className="text-sm text-gray-500 mt-1">Please let us know if you can make it</p>
        </div>
      </div>

      {/* RSVP Form */}
      <form onSubmit={handleSubmit} className="max-w-lg mx-auto px-4 pb-12">
        {/* Event cards */}
        {events && events.length > 0 && (
          <div className="space-y-4 mb-6">
            {events.map((event) => {
              const resp = responses[event.id] || {};
              const isAttending = resp.attending === 'yes';
              return (
                <div
                  key={event.id}
                  className="bg-white rounded-2xl border border-gray-200 overflow-hidden"
                >
                  {/* Event header */}
                  <div className="px-6 py-4 border-b border-gray-100" style={{ backgroundColor: `${primaryColor}08` }}>
                    <h3 className="font-semibold text-gray-900">{event.title || event.name}</h3>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
                      {event.event_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(event.event_date)}
                        </span>
                      )}
                      {event.start_time && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {formatTime(event.start_time)}
                        </span>
                      )}
                      {event.venue && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {event.venue}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Attendance selection */}
                  <div className="px-6 py-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">Will you attend?</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: 'yes', label: 'Attending', color: 'border-green-400 bg-green-50 text-green-700' },
                        { value: 'no', label: 'Not Attending', color: 'border-red-400 bg-red-50 text-red-700' },
                        { value: 'maybe', label: 'Maybe', color: 'border-yellow-400 bg-yellow-50 text-yellow-700' }
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => updateResponse(event.id, 'attending', opt.value)}
                          className={`px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${
                            resp.attending === opt.value
                              ? opt.color
                              : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>

                    {/* Meal preferences (shown when attending) */}
                    {isAttending && (
                      <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1.5">
                            <UtensilsCrossed className="w-3.5 h-3.5" />
                            Meal Preference
                          </label>
                          <select
                            value={resp.meal_preference || 'Standard'}
                            onChange={(e) => updateResponse(event.id, 'meal_preference', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                          >
                            {MEAL_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                            Dietary Notes / Allergies
                          </label>
                          <textarea
                            value={resp.dietary_notes || ''}
                            onChange={(e) => updateResponse(event.id, 'dietary_notes', e.target.value)}
                            placeholder="Any allergies or dietary requirements..."
                            rows={2}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Plus one */}
        {guest?.plus_one_allowed && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={plusOne}
                onChange={(e) => setPlusOne(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-500"
              />
              <div className="flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Bringing a plus one?</span>
              </div>
            </label>
            {plusOne && (
              <input
                type="text"
                value={plusOneName}
                onChange={(e) => setPlusOneName(e.target.value)}
                placeholder="Plus one's full name"
                className="mt-3 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
              />
            )}
          </div>
        )}

        {/* Message to the couple */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
            <MessageSquare className="w-4 h-4" />
            Message to the Couple (optional)
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Send your love and well wishes..."
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none"
          />
        </div>

        {/* Error display */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full py-3 rounded-xl text-white font-semibold text-base transition-all disabled:opacity-60 flex items-center justify-center gap-2"
          style={{ backgroundColor: primaryColor }}
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              {data?.rsvps?.length > 0 ? 'Update RSVP' : 'Submit RSVP'}
            </>
          )}
        </button>
      </form>
    </div>
  );
}

export default RSVPForm;
